/**
 * HH Brush PDP — variant dimension updater
 *
 * Listens for Horizon's `variant:update` event (see assets/events.js →
 * VariantUpdateEvent) and updates any `hh-brush-variant-dims` block on
 * the page with the new variant's tuft / belly data. Hides the block if
 * the selected variant has no dimension data.
 *
 * Safe to include globally — no-ops when the block is absent.
 */
(function () {
  'use strict';

  function applyVariant(block, variantId) {
    if (!block) return;

    var raw = block.getAttribute('data-hh-variant-dims');
    if (!raw) {
      block.hidden = true;
      return;
    }

    var map;
    try {
      map = JSON.parse(raw);
    } catch (e) {
      console.warn('[hh-brush-pdp] invalid variant dims JSON', e);
      block.hidden = true;
      return;
    }

    var dims = map[String(variantId)];
    var tuftEl = block.querySelector('[data-hh-dim-tuft]');
    var bellyEl = block.querySelector('[data-hh-dim-belly]');
    var overallEl = block.querySelector('[data-hh-dim-overall]');
    var tuftCell = block.querySelector('[data-hh-dim-cell="tuft"]');
    var bellyCell = block.querySelector('[data-hh-dim-cell="belly"]');
    var overallCell = block.querySelector('[data-hh-dim-cell="overall"]');

    if (!dims || (dims.tuft == null && dims.belly == null)) {
      block.hidden = true;
      return;
    }

    if (tuftEl) tuftEl.textContent = dims.tuft != null ? dims.tuft + ' mm' : '';
    if (bellyEl) bellyEl.textContent = dims.belly != null ? dims.belly + ' mm' : '';
    if (overallEl) overallEl.textContent = dims.overall != null ? dims.overall + ' mm' : '';
    if (tuftCell) tuftCell.hidden = dims.tuft == null;
    if (bellyCell) bellyCell.hidden = dims.belly == null;
    if (overallCell) overallCell.hidden = dims.overall == null;
    block.hidden = false;
  }

  function handleVariantUpdate(event) {
    var resource = event && event.detail && event.detail.resource;
    var productId = event && event.detail && event.detail.data && event.detail.data.productId;
    if (!resource || !resource.id) return;

    // Variant dimension block (Session 25)
    var dimsSelector = productId
      ? '[data-hh-variant-dims-product="' + productId + '"]'
      : '[data-hh-variant-dims-product]';
    document.querySelectorAll(dimsSelector).forEach(function (block) {
      applyVariant(block, resource.id);
    });

    // Size chart row highlight (Session 26 — Tab: Sizes)
    var chartSelector = productId
      ? '[data-hh-size-chart-product="' + productId + '"]'
      : '[data-hh-size-chart-product]';
    document.querySelectorAll(chartSelector).forEach(function (chart) {
      applySelectedRow(chart, resource.id);
    });
  }

  function applySelectedRow(chart, variantId) {
    if (!chart) return;
    var rows = chart.querySelectorAll('[data-variant-id]');
    var targetId = String(variantId);
    rows.forEach(function (row) {
      if (row.getAttribute('data-variant-id') === targetId) {
        row.classList.add('hh-brush-tab-sizes__row--active');
      } else {
        row.classList.remove('hh-brush-tab-sizes__row--active');
      }
    });
  }

  document.addEventListener('variant:update', handleVariantUpdate);

  // ----------------------------------------------------------------
  // Size chart row click → select variant via Horizon's variant-picker
  // ----------------------------------------------------------------
  function selectVariantFromRow(variantId, productId, row) {
    if (row) {
      var chart = row.closest('[data-hh-size-chart-product]');
      if (chart) applySelectedRow(chart, variantId);
    }

    var picker = document.querySelector(
      'variant-picker[data-product-id="' + productId + '"]'
    );
    if (!picker) return;

    var input = picker.querySelector('[data-variant-id="' + variantId + '"]');
    if (!input) return;

    if (input.tagName === 'INPUT') {
      input.click();
    } else if (input.tagName === 'OPTION') {
      var select = input.closest('select');
      if (select) {
        select.value = input.value;
        select.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
  }

  function initRowClick(root) {
    var charts = (root || document).querySelectorAll('[data-hh-size-chart-product]');
    charts.forEach(function (chart) {
      if (chart.dataset.hhRowClickInit === 'true') return;
      chart.dataset.hhRowClickInit = 'true';
      var productId = chart.getAttribute('data-hh-size-chart-product');

      chart.addEventListener('click', function (event) {
        var row = event.target.closest('[data-variant-id]');
        if (!row) return;
        selectVariantFromRow(row.getAttribute('data-variant-id'), productId, row);
      });

      chart.addEventListener('keydown', function (event) {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        var row = event.target.closest('[data-variant-id]');
        if (!row) return;
        event.preventDefault();
        selectVariantFromRow(row.getAttribute('data-variant-id'), productId, row);
      });
    });
  }

  // ----------------------------------------------------------------
  // mm / in display toggle
  // ----------------------------------------------------------------
  var LS_KEY = 'hhBrushUnit';

  function getPreferredUnit() {
    try { return localStorage.getItem(LS_KEY) || 'mm'; }
    catch (e) { return 'mm'; }
  }

  function setPreferredUnit(unit) {
    try { localStorage.setItem(LS_KEY, unit); } catch (e) {}
  }

  function applyUnit(unit) {
    document.querySelectorAll('[data-hh-mm]').forEach(function (cell) {
      var mm = parseFloat(cell.getAttribute('data-hh-mm'));
      if (isNaN(mm)) return;
      if (unit === 'in') {
        cell.textContent = (mm / 25.4).toFixed(2) + ' in';
      } else {
        cell.textContent = (mm % 1 === 0 ? mm.toFixed(1) : String(mm)) + ' mm';
      }
    });
    document.querySelectorAll('[data-hh-unit]').forEach(function (btn) {
      var isActive = btn.getAttribute('data-hh-unit') === unit;
      btn.classList.toggle('hh-brush-tab-sizes__unit-btn--active', isActive);
      btn.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    });
  }

  function initUnitToggle(root) {
    var toggles = (root || document).querySelectorAll('.hh-brush-tab-sizes__unit-toggle');
    toggles.forEach(function (toggle) {
      if (toggle.dataset.hhUnitInit === 'true') return;
      toggle.dataset.hhUnitInit = 'true';
      toggle.addEventListener('click', function (event) {
        var btn = event.target.closest('[data-hh-unit]');
        if (!btn) return;
        var unit = btn.getAttribute('data-hh-unit');
        setPreferredUnit(unit);
        applyUnit(unit);
      });
    });
    applyUnit(getPreferredUnit());
  }

  /* ----------------------------------------------------------------
   * Judge.me refresh — re-init widget when Reviews tab opens.
   * Judge.me may skip init on hidden panels; this ensures the widget
   * renders when the tab becomes visible.
   * ---------------------------------------------------------------- */
  function maybeRefreshJudgeMe() {
    if (window.jdgm && typeof window.jdgm.customizableWidgets === 'function') {
      try { window.jdgm.customizableWidgets(); } catch (e) { /* silent */ }
    }
  }

  /* ----------------------------------------------------------------
   * Tab switching — desktop tabs, mobile accordion
   * ---------------------------------------------------------------- */
  function isMobileLayout() {
    return window.matchMedia && window.matchMedia('(max-width: 749px)').matches;
  }

  function activateTab(container, tabId) {
    if (!container) return;
    var triggers = container.querySelectorAll('[role="tab"]');
    var panels = container.querySelectorAll('[role="tabpanel"]');
    var mobile = isMobileLayout();

    triggers.forEach(function (t) {
      var isThis = t.getAttribute('data-hh-tab-id') === tabId;
      t.setAttribute('aria-selected', isThis ? 'true' : 'false');
      t.setAttribute('tabindex', isThis ? '0' : '-1');
      if (isThis) {
        t.classList.add('hh-brush-tabs__trigger--active');
      } else {
        t.classList.remove('hh-brush-tabs__trigger--active');
      }
    });

    panels.forEach(function (p) {
      var isThis = p.id === 'hh-tab-panel-' + tabId;
      if (mobile) {
        // Accordion: panel follows its trigger's selected state independently.
        // But activateTab() swaps single "active" — on mobile we want toggle
        // behavior so multiple panels can be open. Caller handles this.
        p.hidden = !isThis;
        if (isThis) p.classList.add('hh-brush-tabs__panel--active');
      } else {
        // Desktop: exactly one panel visible.
        p.hidden = !isThis;
        if (isThis) {
          p.classList.add('hh-brush-tabs__panel--active');
        } else {
          p.classList.remove('hh-brush-tabs__panel--active');
        }
      }
    });

    var activePanel = container.querySelector('#hh-tab-panel-' + tabId);
    if (activePanel && activePanel.querySelector('.jdgm-review-widget')) {
      maybeRefreshJudgeMe();
    }
  }

  function toggleMobilePanel(container, tabId) {
    if (!container) return;
    var trigger = container.querySelector('[data-hh-tab-id="' + tabId + '"]');
    var panel = container.querySelector('#hh-tab-panel-' + tabId);
    if (!trigger || !panel) return;
    var isOpen = trigger.getAttribute('aria-selected') === 'true';

    if (!isOpen) {
      container.querySelectorAll('[role="tab"][aria-selected="true"]').forEach(function (t) {
        if (t === trigger) return;
        t.setAttribute('aria-selected', 'false');
        t.classList.remove('hh-brush-tabs__trigger--active');
      });
      container.querySelectorAll('.hh-brush-tabs__panel--active').forEach(function (p) {
        if (p === panel) return;
        p.hidden = true;
        p.classList.remove('hh-brush-tabs__panel--active');
      });
    }

    trigger.setAttribute('aria-selected', isOpen ? 'false' : 'true');
    panel.hidden = isOpen;
    if (isOpen) {
      trigger.classList.remove('hh-brush-tabs__trigger--active');
      panel.classList.remove('hh-brush-tabs__panel--active');
    } else {
      trigger.classList.add('hh-brush-tabs__trigger--active');
      panel.classList.add('hh-brush-tabs__panel--active');
      if (panel.querySelector('.jdgm-review-widget')) {
        maybeRefreshJudgeMe();
      }
    }
  }

  function handleTriggerClick(container, trigger) {
    var tabId = trigger.getAttribute('data-hh-tab-id');
    if (isMobileLayout()) {
      toggleMobilePanel(container, tabId);
    } else {
      activateTab(container, tabId);
    }
  }

  function attachTriggerListeners(container) {
    var triggers = container.querySelectorAll('[role="tab"][data-hh-tab-id]');
    triggers.forEach(function (trigger) {
      if (trigger.dataset.hhTabListener === 'true') return;
      trigger.dataset.hhTabListener = 'true';
      trigger.addEventListener('click', function () {
        handleTriggerClick(container, trigger);
      });
    });
  }

  // Fallback delegation — handles any triggers that slip past init
  // (e.g. blocks added post-DOM-ready). Class selector is more robust
  // than custom element tag selector across browsers/contexts.
  document.addEventListener('click', function (event) {
    var trigger = event.target.closest('[role="tab"][data-hh-tab-id]');
    if (!trigger) return;
    if (trigger.dataset.hhTabListener === 'true') return;
    var container = trigger.closest('.hh-brush-tabs');
    if (!container) return;
    handleTriggerClick(container, trigger);
  });

  // Initial activation: on DOM ready, activate the first tab in each
  // <hh-brush-tabs> container. Blocks render all panels with `hidden`
  // so nothing shows until JS runs; this init makes the first tab
  // visible. Re-runs on Theme Editor section reload.
  function initTabs(root) {
    var containers = (root || document).querySelectorAll('.hh-brush-tabs');
    containers.forEach(function (container) {
      attachTriggerListeners(container);
      if (container.dataset.hhTabsInitialized === 'true') return;
      container.dataset.hhTabsInitialized = 'true';
      if (isMobileLayout()) return;
      var firstTrigger = container.querySelector('[role="tab"][data-hh-tab-id]');
      if (!firstTrigger) return;
      var tabId = firstTrigger.getAttribute('data-hh-tab-id');
      activateTab(container, tabId);
    });
    initRowClick(root);
    initUnitToggle(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initTabs(); });
  } else {
    initTabs();
  }

  // Theme Editor: re-init when a section is reloaded.
  document.addEventListener('shopify:section:load', function (event) {
    initTabs(event.target);
  });

  // (Hero rating → reviews smooth-scroll now lives globally in hh-custom.js so it
  //  serves both the brushes credibility link and the bases hero rating.)

  // Keyboard navigation (arrow keys) on desktop tabs
  document.addEventListener('keydown', function (event) {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
    var trigger = event.target.closest('[role="tab"][data-hh-tab-id]');
    if (!trigger) return;
    if (isMobileLayout()) return;

    var container = trigger.closest('.hh-brush-tabs');
    if (!container) return;

    var triggers = Array.from(container.querySelectorAll('[role="tab"]'));
    var idx = triggers.indexOf(trigger);
    var nextIdx = event.key === 'ArrowRight' ? idx + 1 : idx - 1;
    if (nextIdx < 0) nextIdx = triggers.length - 1;
    if (nextIdx >= triggers.length) nextIdx = 0;

    var nextTrigger = triggers[nextIdx];
    var nextTabId = nextTrigger.getAttribute('data-hh-tab-id');
    nextTrigger.focus();
    activateTab(container, nextTabId);
    event.preventDefault();
  });

})();
